import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef } from "react"

import { useGetTestTemplateProducts } from "../hooks"
import { getTestTemplateComparisonLabel, getTestTemplateResultUomLabel } from "../utils"

import { Button } from "@/components/common/Button"
import DateChip from "@/components/DateChip"
import { ProductStatusCell } from "@/components/table"
import type { TestTemplate } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"

interface TestTemplateProductsDialogProps {
  testTemplate: TestTemplate
  onClose: () => void
}

const TestTemplateProductsDialog: FC<TestTemplateProductsDialogProps> = ({ testTemplate, onClose }) => {
  const { products, isFetching, loadMore } = useGetTestTemplateProducts(testTemplate.testTemplateId)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [throttledLoadMore] = useThrottle(() => loadMore(), 200)

  const handleScroll = useScrollLoadMore({
    fetching: isFetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: containerRef,
  })

  return (
    <Dialog open fullWidth maxWidth='xl' onClose={onClose} data-testid='test-template-products-dialog'>
      <DialogTitle className='pl-10 font-normal text-primary'>Quality Test Template Associated Products</DialogTitle>
      <IconButton className='absolute right-4 top-3' aria-label='Close' onClick={onClose}>
        <CloseOutlined className='text-blue-gray' />
      </IconButton>
      <Divider />
      <DialogContent className='p-10' ref={containerRef} onScroll={handleScroll}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test Number</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Results UoM</TableCell>
              <TableCell>Comparison</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{testTemplate.externalSystemId}</TableCell>
              <TableCell>{testTemplate.description}</TableCell>
              <TableCell>{getTestTemplateResultUomLabel(testTemplate.resultsUnitOfMeasure ?? "")}</TableCell>
              <TableCell>{getTestTemplateComparisonLabel(testTemplate.comparison)}</TableCell>
              <TableCell>
                <DateChip date={testTemplate.createdOn} />
              </TableCell>
              <TableCell>
                <DateChip date={testTemplate.updatedOn} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={6} className='py-6'>
                <Typography variant='subtitle1' component='h3' className='mb-4'>
                  Associated Products
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow className='bg-primary-100'>
                      <TableCell className='bg-primary-100'>Product ID</TableCell>
                      <TableCell className='bg-primary-100'>Akrochem Desc.</TableCell>
                      <TableCell className='bg-primary-100'>Supplier Desc.</TableCell>
                      <TableCell className='bg-primary-100'>Supplier Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>
                          <ProductStatusCell product={product} />
                        </TableCell>
                        <TableCell>{product.companyProductDescription}</TableCell>
                        <TableCell>{product.vendorDescription}</TableCell>
                        <TableCell>{product.company?.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {isFetching && (
                  <Box className='mt-6 flex w-full justify-center'>
                    <CircularProgress />
                  </Box>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions className='justify-end px-10 pb-10'>
        <Button data-testid='submit-button' variant='primary' onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TestTemplateProductsDialog
